import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { attentionHeadline } from "@/lib/kpi-labels";
import { carryFilters } from "@/lib/filters";
import { tonePillClass } from "@/lib/tone";
import { dataReference, freshnessBoundMonth, partialMonths, selectGrainSeries } from "@/lib/time-range";
import { DataIssueBanner, UpdatedStamp } from "@/components/data";

import { getFreshness, getOverview, messageFor } from "./api";
import { CostStackPanel } from "./components/CostStackPanel";
import { YieldHeadline } from "./components/YieldHeadline";
import { YieldTrend } from "./components/YieldTrend";
import { YieldControls, useControls } from "./controls";
import { headlineWindow } from "./window";
import {
  DATA_FRESH_MAX_DAYS,
  freshnessState,
  regionFromDeepLink,
  regionLabel,
  regionsFrom,
  severityTone,
} from "./format";

/**
 * The all-regions view.
 *
 * The per-region freshness strip is not decoration. This tile AGGREGATES every
 * region, so its own latest date is the FRESHEST region's - a market that
 * stopped reporting a fortnight ago is invisible in the headline and visible
 * only here. Meteoric learned that the hard way; the strip carries across
 * unchanged in intent.
 */
export function OverviewTab({ areaName }: { areaName: string }) {
  const controls = useControls();

  const overview = useQuery({
    queryKey: ["meteoric_profit_yield", "overview"],
    queryFn: getOverview,
  });

  const freshness = useQuery({
    queryKey: ["meteoric_profit_yield", "freshness"],
    queryFn: getFreshness,
  });

  if (overview.isPending) return <Skeleton className="h-96 w-full" />;

  if (overview.isError) {
    return (
      <Alert>
        <AlertTitle>Profit Yield unavailable</AlertTitle>
        <AlertDescription>{messageFor(overview.error, areaName)}</AlertDescription>
      </Alert>
    );
  }

  const { tile, secondary, attention_items: attention } = overview.data.data;
  // Trim against the DATA's freshness date, exactly as RegionTab does - the
  // artefact's Monday-morning `generated_at` made the just-ended week look
  // closed here while the region tabs (rightly) dropped it (bug 3e90402f).
  const reference = dataReference(overview.data.meta.data_as_of, overview.data.data.generated_at);
  // W1: a monthly series that has not yet reached last month's close is
  // stale for a monthly grain - the tile's flattened `series` (whatever
  // window the producer last computed) is a truer plot than a monthly
  // series stuck behind its own freshness bound.
  const monthlyBound = freshnessBoundMonth(reference);
  const monthlyReaches =
    (tile.series_monthly?.length ?? 0) > 0 &&
    (tile.series_monthly.at(-1)?.m ?? "") >= monthlyBound;
  // Raw (untrimmed) - the still-open current month a chart's own trim
  // removes is exactly the bucket `partialMonths` looks for; a windowed
  // `points` series has already had it cut.
  const rawMonthly = (monthlyReaches ? tile.series_monthly : tile.series).filter((p) => p.present);
  const points = selectGrainSeries(
    {
      daily: tile.series_daily,
      weekly: tile.series_weekly,
      monthly: monthlyReaches ? tile.series_monthly : tile.series,
    },
    controls.grain,
    controls.range,
    reference,
  ).filter((p) => p.present);
  const head = headlineWindow(tile, points, controls.grain, controls.range, reference);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <YieldControls controls={controls} />
        <UpdatedStamp generatedAt={overview.data.data.generated_at} />
      </div>

      {/* A failed freshness read is stated, never silently dropped: "we could
          not check the regions" and "every region is current" look identical
          when the strip simply does not render. */}
      {freshness.isError ? (
        <Alert>
          <AlertTitle>Region freshness could not be checked</AlertTitle>
          <AlertDescription>
            The headline below still shows, but it aggregates every region - a market that stopped
            reporting would not be visible in it.
          </AlertDescription>
        </Alert>
      ) : (
        freshness.data && <FreshnessStrip freshness={freshness.data.data} />
      )}

      <DataIssueBanner issues={controls.grain === "monthly" ? partialMonths(rawMonthly, reference) : []} />

      <YieldHeadline
        tile={tile}
        secondary={secondary}
        trend={<YieldTrend points={points} grain={controls.grain} reference={reference} />}
        window={head.window}
        note={head.note}
      />

      <CostStackPanel controls={controls} areaName={areaName} />

      {attention.length > 0 && (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <h2 className="text-base font-semibold text-ks-ink">Attention items</h2>
            <ul className="flex flex-col divide-y divide-border">
              {attention.map((item) => {
                const region = regionFromDeepLink(item.deep_link);
                const content = (
                  <div className="flex min-h-11 items-center gap-3 py-2 text-sm">
                    <span
                      aria-hidden
                      className={`h-2 w-2 rounded-full ${
                        item.severity === "critical"
                          ? "bg-ks-fail"
                          : item.severity === "warning"
                            ? "bg-ks-warn"
                            : "bg-ks-grey-2"
                      }`}
                    />
                    <Badge
                      variant="outline"
                      className={`capitalize ${tonePillClass(severityTone(item.severity))}`}
                    >
                      {item.severity}
                    </Badge>
                    <span className="text-ks-ink">{attentionHeadline(item.headline, tile.kpi_id, tile.kpi_name)}</span>
                    {region && <ChevronRight aria-hidden className="ml-auto size-4 text-muted-foreground" />}
                  </div>
                );
                return (
                  <li key={`${item.severity}-${item.headline}`} className="hover:bg-muted">
                    {region ? (
                      <Link
                        to="/app/$area/$module/$tab"
                        params={{ area: "customer_acquisition", module: "meteoric_profit_yield", tab: region }}
                        // The region opens on the same range/period as this page (KET-6592).
                        search={((prev: Record<string, unknown>) => carryFilters(prev)) as never}
                        data-nonmutating
                      >
                        {content}
                      </Link>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FreshnessStrip({ freshness }: { freshness: Record<string, string | null> }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Region data through
        </span>
        {regionsFrom(freshness).map(({ region, date }) => {
          const state = freshnessState(date);
          return (
            <span key={region} className="flex items-center gap-2 text-sm">
              <span className="font-medium text-ks-ink">{regionLabel(region)}</span>
              <span className="tabular-nums text-muted-foreground">{date ?? "-"}</span>
              <Badge variant="outline" className={tonePillClass(state.tone)}>
                {state.word}
                {state.ageLabel ? ` - ${state.ageLabel}` : ""}
              </Badge>
            </span>
          );
        })}
        {/* The threshold is written down where the badges are. A reader who
            cannot see what "Lagging" means cannot act on it. */}
        <span className="text-xs text-muted-foreground">Live is within {DATA_FRESH_MAX_DAYS} days of today.</span>
      </CardContent>
    </Card>
  );
}
